import './init/log.js';
import log from 'loglevel';
import VhyReporter from '@vhallyun/log-reporter';
import VideoModule from './core/VideoModule';
import { PlayerEvent } from './core/PlayerEvents';
import VhallInterface from './VhallInterface';
import {
  loadMsgScript,
  extname,
  hasUsableSWF,
  probe,
  judgeX5,
  loadStyles,
  urljoin,
  checkDanmakuSetting,
  isSupportPictureInPicture
} from './common/Utils';
import { systemProbe, getNetworkType } from './core/utils/util';
import { ErrorCode, rc } from './common/error-code';
import axios from 'axios';
import { EVENTS, KV } from './common/Constant';
import API from './message/Api';
import Store from './common/Store';
import handleOption, { getSomeConfig } from './options/index';

// 设置日志上报地址
VhyReporter.setConfig({ uploadUrl: process.env.REPORT_SERVER, __debug: 0 });

// 静态入口
const VhallPlayer = {};
VhallPlayer.VERSION = process.env.VERSION;
VhallPlayer.SUBVERSION = process.env.SUBVERSION;

// 挂载事件名
// 这是先前的方式，直接挂载到VhallPlayer上，不建议使用
for (let i in EVENTS) {
  VhallPlayer[i] = EVENTS[i];
}
// 2022-11-25 新增，建议从 EVENTS 属性上获取事件名称。
VhallPlayer.EVENTS = EVENTS;

//挂载静态方法
// 浏览器兼容性探测
VhallPlayer.probe = probe;
// 检测系统环境信息
VhallPlayer.systemProbe = systemProbe;
// 弹幕设置 校验方法,好像没用，与先前保持一致
VhallPlayer.checkDanmakuSetting = checkDanmakuSetting;
// 检查是否支持画中画
VhallPlayer.isSupportPictureInPicture = isSupportPictureInPicture;

/**
 * 创建实例
 * @param {object} options 创建实例所需配置
 * @param {function} success 成功回调
 * @param {function} failure 失败回调
 */
VhallPlayer.createInstance = async function (options, success, failure) {
  if (typeof success !== 'function') success = null;
  if (typeof failure !== 'function') failure = null;

  const pagetimestamp = parseInt(performance.timeOrigin) || 
                          parseInt(performance.timing?.navigationStart) || 
                          Date.now()
  const now = Date.now();
  let timeline = {
    "t_playertimestamp": now,
    "t_createinstance": Math.max(now - pagetimestamp, 0),
  };

  // 初始化参数校验及预处理
  log.info('创建播放器实例参数：', JSON.parse(JSON.stringify(options)));
  let opt;
  try {
    opt = handleOption(JSON.parse(JSON.stringify(options)));
  } catch (err) {
    failure?.(Object.assign({ errdesc: err.message }, ErrorCode.INVALID_PARAM));
    return;
  }
  log.info('预处理后参数：', JSON.parse(JSON.stringify(opt)));

  // 注册日志上报的公用字段
  _registerReporterSuperProperties(opt);

  // 初始化上报的通用code
  const initKey = opt.type === 'live' ? 102010 : 92010;

  // 存储部分公用参数
  const store = _createStore(opt);

  // 检查并加载消息js
  try {
    await loadMsgScript();
    timeline["t_msgjs"] = Date.now() - timeline["t_playertimestamp"];
  } catch (ex) {
    VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.BASEKS_LOAD_FAILED), opt));
    failure?.(ErrorCode.BASEKS_LOAD_FAILED);
    return;
  }

  const isLive = opt.isLive;
  // 检测是否需要使用flash播放，以及当前浏览器对flash的支持情况
  const useSWF = isLive ? opt.liveOption.useSWF : opt.vodOption.useSWF;
  // 运行环境检测
  const context = systemProbe();
  // flash播放检查
  if (!_checkFlash(useSWF, context, opt)) {
    VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.NOT_FOUND_FLASH), opt));
    failure?.(ErrorCode.NOT_FOUND_FLASH);
    return;
  }

  //调用微吼云初始化业务接口
  let initData = null;
  let playerData = null;
  const api = new API(store);
  try {
    // 调用 /sdk/v2/init/start 接口获取上报日志、消息长连接地址、文档相关等信息的。不影响直播点播功能。
    initData = await api.init();
    timeline["t_vhallyunsdkinit"] = Date.now() - timeline["t_playertimestamp"];
    if (isLive) {
      // 通过 /sdk/v2/room/get-watch-info 接口获取直播相关数据
      playerData = await api.initLive(opt.liveOption.roomId);
    } else {
      // 通过 /sdk/v2/demand/get-record-watch-info 接口获取点播相关数据
      playerData = await api.initVod(opt.vodOption.recordId);
    }
    timeline["t_vhallyungetinfo"] = Date.now() - timeline["t_playertimestamp"];
  } catch (err) {
    const errMsg = (err && err.msg) || undefined;
    switch (errMsg) {
      case '房间查询为空':
        VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.ROOMID_NOT_EXIST), opt));
        failure?.(ErrorCode.ROOMID_NOT_EXIST);
        break;
      case 'AccessToken格式不正确':
        VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.TOKEN_NOT_EXIST), opt));
        failure?.(ErrorCode.TOKEN_NOT_EXIST);
        break;
      case '应用查询为空':
        VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.APPID_NOT_EXIST), opt));
        failure?.(ErrorCode.APPID_NOT_EXIST);
        break;
      default:
        VhyReporter.scout(initKey, Object.assign(rc(ErrorCode.INIT_SERVERDATA_FAILED, errMsg), opt));
        failure?.(ErrorCode.INIT_SERVERDATA_FAILED);
        break;
    }
    return;
  }

  // 是否点播加密解密播放
  let needDecrypt = false;
  if (!isLive) {
    // 点播加密的hls视频时, FlashPlayer和原生播放器播非加密视频, HlsPlayer播加密视频
    const isHls = extname(playerData.default_server.uri) === '.m3u8';
    const isFlash = (typeof useSWF === 'undefined' && context.browser === 'ie') || useSWF === true;
    needDecrypt = !!(playerData?.safe_server?.uri && isHls && !isFlash && context.supportMSE);
  }

  // 服务端数据合并
  const serverData = Object.assign({}, playerData, { log_server: initData.log_server, needDecrypt });
  // 日志上报配置
  const reportConfig = getSomeConfig('reportConfig', opt, serverData);
  // 调度插件配置
  const schedulerConfig = getSomeConfig('schedulerConfig', opt, serverData);
  // 从服务端拿到了一部分数据，需要进行追加部分公用字段
  _registerReporterSuperProperties(Object.assign({}, reportConfig, schedulerConfig), true);
  // 直播时移插件配置
  const liveTimeshiftConfig = getSomeConfig('liveTimeshiftConfig', opt, serverData);
  // 水印插件配置
  const watermarkConfig = getSomeConfig('watermarkConfig', opt, serverData);
  // 点播字幕插件配置
  const vodSubtitleConfig = getSomeConfig('vodSubtitleConfig', opt, serverData);
  // 直播字幕插件配置
  const liveSubtitleConfig = getSomeConfig('liveSubtitleConfig', opt, serverData);
  // 弹幕配置
  const barrageConfig = getSomeConfig('barrageConfig', opt, serverData);

  // 调用内核创建player实例
  const vhallplayer = new VideoModule();
  // 主题相关处理
  _initTheme(vhallplayer, opt);
  /*
  有部分事件，有几率在调用成功回调前被触发，以至于用户监听的相关事件被漏掉
  以下逻辑为在处理此种情况下，补发相关事件。
  */
  _initEventPatches();
  let params = {};
  try {
    params = {
      timeline,
      id: opt.videoNode, // dom节点
      x5: !!judgeX5(),
      autoplay: opt.autoplay !== false,
      autoplayMuteAttempt: opt.autoplayMuteAttempt == true,
      muted: opt.muted == true,
      accountId: opt.accountId,
      // 视频类型
      type: isLive ? opt.liveOption.type : playerData.default_server.mp4_domainnames ? 'native' : 'hls',
      isLive: isLive, // 是否是直播
      liveOption: isLive ? opt.liveOption : null,
      vodOption: isLive ? null : opt.vodOption,
      videoStyleOption: opt.videoStyleOption,
      poster: opt.poster, // 封面图
      // 点播视频路径
      vod_video_uri: isLive ? null : needDecrypt ? playerData.safe_server.uri : playerData.default_server.uri,
      // nativeurl: isLive ? null : playerData.dispatch_server + playerData.default_server.uri,
      plugin_reporter: JSON.stringify(reportConfig),
      // otherOption, 自定义上报字段，此参数内的字段均透传上报。公司内部使用，不对外。
      other_option: JSON.stringify(opt.otherOption || {}), // 自定义上报字段
      plugin_scheduler: JSON.stringify(schedulerConfig),
      plugin_timeshift: JSON.stringify(liveTimeshiftConfig),
      plugin_watermark: JSON.stringify(watermarkConfig),
      plugin_barrage: JSON.stringify(barrageConfig),
      plugin_vodSubtitle: JSON.stringify(vodSubtitleConfig),
      plugin_liveSubtitle: JSON.stringify(liveSubtitleConfig),
      plugin_marquee: JSON.stringify(opt.marqueeOption),
      plugin_pursue: JSON.stringify(opt.pursueOption),
      plugin_thorn: JSON.stringify(opt.thornOption),
      peer5Option: {
        open: false, // 默认关闭
        customerId: '', // todo 默认customerId - 考虑从接口获取
        fallback: true, // peer5 sdk 加载失败时，回退到未启用状态；false为报错，初始播放器失败
        ...(opt.peer5Option || {})
      },
      _uuid: opt._uuid
    };
    log.info('创建VideoModule参数：', JSON.parse(JSON.stringify(params)));

    // player 调用 init 方法进行内核方面的初始化
    await vhallplayer.init(params); // => 创建播放器、载入插件、设置线路
    if (!vhallplayer.player) {
      log.error('初始化播放器失败: ', ErrorCode.INIT_CORE_FAILED);
      failure?.(ErrorCode.INIT_CORE_FAILED);
      return;
    }
  } catch (err) {
    log.error('初始化播放器失败: ', err);
    VhyReporter.scout(initKey, Object.assign({ errdesc: err.message }, rc(ErrorCode.INIT_FAILED), opt, params));
    failure?.(Object.assign({ errdesc: err.message }, ErrorCode.INIT_FAILED));
  }

  /**
   * 初始化事件
   * @param {*} vhallplayer
   */
  function _initEventPatches() {
    const isLive = opt.type === 'live';
    let success_flag = false;
    let loadedmetadata_flag = false;
    let progress_flag = false;
    let play_flag = false;
    let loadeddata_flag = false;
    let loadedVodSubtitle_flag = false;
    vhallplayer.on(VhallPlayer.PROGRESS_IN, e => {
      // 成功回调标识，预示着触发此事件时还未执行成功回调。以下皆同
      if (success_flag !== true) {
        progress_flag = { data: e };
      }
      // 转发，为了成功回调后补发事件时不至于把播放器的事件又发回给了播放器。故事件名做了区分
      vhallplayer.emit2All(VhallPlayer.PROGRESS, e);
    });

    vhallplayer.on(VhallPlayer.LOADEDMETADATA_IN, e => {
      if (success_flag !== true) {
        loadedmetadata_flag = { data: e };
      }
      vhallplayer.emit2All(VhallPlayer.LOADEDMETADATA, e);
    });

    vhallplayer.on(VhallPlayer.PLAY_IN, e => {
      if (success_flag !== true) {
        play_flag = { data: e };
      }
      vhallplayer.emit2All(VhallPlayer.PLAY, e);
    });

    vhallplayer.on(VhallPlayer.LOADED_IN, e => {
      if (success_flag !== true) {
        loadeddata_flag = { data: e };
      }
      vhallplayer.emit2All(VhallPlayer.LOADED, e);
    });

    vhallplayer.on('vodSubtitleLoadedIn', e => {
      if (success_flag !== true) {
        loadedVodSubtitle_flag = { data: e };
      }
      vhallplayer.emit2All(VhallPlayer.VOD_SUBTITLE_LOADED, e);
    });

    // 调度失败
    vhallplayer.once('schedulerfail', () => {
      log.error('[schedulerfail]初始化获取调度信息失败');
      failure?.(Object.assign({ errdesc: '初始化获取调度信息失败' }, ErrorCode.INIT_FAILED));
    });

    // ready事件。由调度插件完成后抛出。
    vhallplayer.on(PlayerEvent.READY, async () => {
      log.info('收到ready事件');
      let markData;
      let aiHightlightData;
      if (!isLive && playerData.cue_point !== '') {
        await axios.get(playerData.cue_point).then(
          res => {
            markData = res.data.usrdata;
            aiHightlightData = res.data.ai_highlight;
            // 派发cue_point文件到文档SDK
            window.VhallMsg && VhallMsg.emitBroadcast({ type: 'cue_point', data: JSON.stringify(res.data) });
            // 记录至缓存
            store.setKV('cuepoint', JSON.stringify(res.data));

            // 派发播放进度到文档SDK
            vhallplayer.on('timeupdate', t => {
              window.VhallMsg && VhallMsg.emitBroadcast({ type: 'time_update', data: t });
            });
          },
          () => {
            log.warn('cue_point文件请求失败');
          }
        );
      } else if (isLive) {
        vhallplayer.on('timeupdate', e => {
          window.VhallMsg && VhallMsg.emitBroadcast({ type: 'delay', data: { delay: e.delay } });
        });
      }

      // 播放器初始化成功上报
      VhyReporter.scout(initKey, Object.assign({}, opt, params, rc(ErrorCode.SUCCESSFUL)));

      success_flag = true;

      // 播放器内核、接口数据都初始完成，调用 VhallInterface 初始化外壳UI及API
      const playerInstance = new VhallInterface({
        vhallplayer,
        isLive: isLive,
        videoNode: `#${opt.videoNode}`,
        store,
        markPoints: markData || [],
        markPointsConfig: opt.markPointsOption,
        aiHightlights: aiHightlightData || [],
        barrageSetting: JSON.parse(params.plugin_barrage),
        api: api
      });
      //实例结果添加至window，便于调试问题
      playerInstance._originalOption = options;
      window.vp = playerInstance;
      //回调实例结果
      success?.({
        vhallplayer: playerInstance,
        markPoints: markData || [],
        aiHightlights: aiHightlightData || [],
      });

      // 注意此处补发事件！
      // 标识在执行成功回调前是否已经触发了此类事件，如有则补发事件
      if (progress_flag) {
        vhallplayer.emit2All(VhallPlayer.PROGRESS, progress_flag.data);
      }
      if (loadedmetadata_flag) {
        vhallplayer.emit2All(VhallPlayer.LOADEDMETADATA, loadedmetadata_flag.data);
      }
      if (play_flag) {
        vhallplayer.emit2All(VhallPlayer.PLAY, play_flag.data);
      }
      if (loadeddata_flag) {
        vhallplayer.emit2All(VhallPlayer.LOADED, loadeddata_flag.data);
      }
      if (loadedVodSubtitle_flag) {
        vhallplayer.emit2All(VhallPlayer.VOD_SUBTITLE_LOADED, loadedVodSubtitle_flag.data);
      }
    });
  }
};

/**
 * 存储部分公用参数
 * @param {object} opt
 */
function _createStore(opt) {
  const store = new Store();
  store.setKV(KV.APP_ID, opt.appId);
  store.setKV(KV.THIRD_PARTY_USER_ID, opt.accountId);
  store.setKV(KV.ACCESS_TOKEN, opt.token);
  // - -! ??????
  store.setKV(KV.PACKAGE_CHECK, 'peter');
  // 客户端类型
  store.setKV(KV.CLIENT, 'pc_browser');
  store.setKV(KV.LANGUAGE, opt.language);
  return store;
}

/**
 * flash播放校验
 * @param {*} useSWF
 * @param {*} context
 * @param {*} opt
 * @returns
 */
function _checkFlash(useSWF, context, opt) {
  // 如果 已经配置使用flash || 未配置但是当前在ie浏览器环境或者当前是rtmp直播
  const isRequireFlash =
    useSWF === true || (useSWF === undefined && (context.browser === 'ie' || opt?.liveOption?.type === 'rtmp'));
  // 要求使用flash播放，但是当前浏览器不支持
  if (isRequireFlash && !hasUsableSWF()) {
    return false;
  }
  return true;
}

/**
 * 初始化播放器主题
 * @param {*} vhallplayer
 * @param {*} opt
 */
function _initTheme(vhallplayer, opt) {
  vhallplayer.theme = opt.theme;
  const playerNode = document.querySelector('#' + opt.videoNode);
  playerNode?.classList?.add(`vhplayer-${vhallplayer.theme}`);
  if (vhallplayer.theme === 'light') {
    const cssUrl = urljoin(process.env.BASE_ASSET_URL, process.env.VERSION, 'style/skins/main.css');
    loadStyles(cssUrl);
  }
}

/**
 * 注册日志上报的公用字段
 * wiki：http://wiki.vhallops.com/pages/viewpage.action?pageId=310543311
 */
function _registerReporterSuperProperties(opt, isAppend) {
  if (!isAppend) {
    const probe = systemProbe();
    const presetParams = {
      _uuid: opt._uuid,
      s: opt._uuid,
      uid: opt.accountId,
      app_id: opt.appId,
      aid: opt.webinar_id,
      p: opt.webinar_id,
      bu: 1,
      ver: process.env.VERSION,
      webUrl: window.location.href,
      ua: navigator.userAgent,
      net: probe.networkType,
      browser_name: probe.browser,
      pf: probe.platform,
      df: probe.platform === 7 ? 'web' : 'wap',
      osv: probe.os,
      ndi: '', // 移动端上报  paas为空
      guid: '', // saas平台上报  paas为空
      vtype: '', // saas平台上报  paas为空
      topic: '' // saas平台上报  paas为空
    };
    VhyReporter.registerSuperProperties(presetParams);
  } else {
    if (opt.session_id) {
      VhyReporter.registerSuperProperties('s', opt.session_id);
    }
    VhyReporter.registerSuperProperties('vid', opt.vid || '');
    VhyReporter.registerSuperProperties('vfid', opt.vfid || '');
    VhyReporter.registerSuperProperties('flow_type', opt.flow_type || '');
    // 默认清晰度
    VhyReporter.registerSuperProperties('picture_quality', opt.defaultDef || '480p');
  }

  // 监听网络变化并更新上报net字段
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    connection.addEventListener('change', function() {
        const networkType = getNetworkType();
        _registerReporterSuperProperties({net: networkType}, true);
    });
  }
}

window.VhallPlayer = VhallPlayer;
